import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Send, XCircle, Bot, User } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { useChatStore } from "../stores/chat";
import { settings } from "../lib/settings";
import LoadingDots from "../components/LoadingDots";
import defaultBotAvatar from "../assets/images/default-bot-avatar.png";
import { useTranslation } from "react-i18next";

const cleanMessageText = (text: string) => {
  return text
    .replace(/【\d+:\d+†source】/g, "")
    .replace(/\[\d+:\d+†source\]/g, "");
};

export default function Chat() {
  const { t } = useTranslation();
  const { chatId } = useParams();
  const navigate = useNavigate();
  const {
    messages,
    sendMessage,
    currentThread,
    fetchMessages,
    threads,
    setCurrentThread,
    fetchThreads,
    isWaitingResponse,
    assistants,
    setSelectedAssistant,
  } = useChatStore();
  const [newMessage, setNewMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Load threads when component mounts
  useEffect(() => {
    const loadData = async () => {
      console.log("Loading threads...");
      try {
        setIsLoading(true);
        setError(null);
        await fetchThreads();
        console.log("Threads loaded successfully");
      } catch (err) {
        console.error("Failed to load threads:", err);
        setError(t("chat.error.load_failed"));
      }
    };

    loadData();
  }, [fetchThreads]);

  // Handle thread and messages loading
  useEffect(() => {
    const loadMessages = async () => {
      console.log("Current state:", {
        chatId,
        threadsCount: threads.length,
        currentThread,
      });

      if (!chatId) {
        // Don't redirect, just show empty state
        setIsLoading(false);
        return;
      }

      // Find thread by thread_id
      const thread = threads.find((t) => t.id === chatId);
      console.log("Found thread:", thread);

      if (thread) {
        console.log("Setting current thread and fetching messages...");
        setCurrentThread(thread);
        try {
          // Use thread.uid for existing threads or thread.thread_uid for newly created ones
          await fetchMessages(thread.id);
          console.log("Messages fetched successfully");
        } catch (err) {
          console.error("Failed to fetch messages:", err);
          setError(t("chat.error.load_failed"));
        }
      } else if (threads.length > 0) {
        console.log("Thread not found in loaded threads");
        setError(t("chat.error.chat_not_found"));
      }

      setIsLoading(false);
    };

    loadMessages();
  }, [chatId, threads, setCurrentThread, fetchMessages]);

  // Get current assistant info
  const getCurrentAssistant = () => {
    if (!currentThread) return null;
    return assistants.find(
      (assistant) => assistant.id === currentThread.bot_id
    );
  };

  const currentAssistant = getCurrentAssistant();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentThread?.id || newMessage.trim() === "" || isSending) return;

    setIsSending(true);
    try {
      await sendMessage(
        currentThread.bot_id,
        currentThread.id,
        newMessage.trim()
      );
      setNewMessage("");
    } catch (error) {
      console.error("Failed to send message:", error);
      setError(t("chat.error.send_failed"));
    } finally {
      setIsSending(false);
    }
  };

  const handleRetry = async () => {
    setError(null);
    setIsLoading(true);
    try {
      await fetchThreads();
      if (chatId && threads.length > 0) {
        const thread = threads.find((t) => t.id === chatId);
        if (thread) {
          setCurrentThread(thread);
          await fetchMessages(thread.id);
        }
      }
    } catch (err) {
      console.error("Retry failed:", err);
      setError(t("chat.error.reload_failed"));
    } finally {
      setIsLoading(false);
    }
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to calculate proper scrollHeight
      textarea.style.height = "0px";

      const scrollHeight = textarea.scrollHeight;
      const maxHeight = 128; // 8rem = 128px
      const newHeight = Math.min(scrollHeight, maxHeight);

      textarea.style.height = `${newHeight}px`;
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [newMessage]);

  const handleSelectAssistant = () => {
    setSelectedAssistant(null);
    navigate("/new-chat");
  };

  console.log("Rendering state:", {
    isLoading,
    error,
    hasMessages: messages.length > 0,
    currentThread,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full bg-[#0F1524]">
        <p className="text-white">{t("chat.loading")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-4">
        <div className="flex flex-col items-center justify-center text-center max-w-md">
          <div className="w-12 h-12 rounded-full bg-[#1A2235] flex items-center justify-center mb-4">
            <XCircle size={24} className="text-[#EF4444]" />
          </div>
          <p className="text-[#EF4444] mb-4 text-lg font-medium">{error}</p>
          <button
            onClick={handleRetry}
            className="px-6 py-3 rounded-xl bg-[#1A2235] text-white hover:bg-[#253045] transition-colors"
          >
            {t("chat.retry")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto overflow-x-hidden">
      {/* Chat Header with Thread Name */}
      {currentThread && chatId && (
        <div className="bg-[#0F1524] px-8 py-2 border-b border-[#1A2235]">
          <div className="max-w-5xl mx-auto w-full flex items-center">
            <div className="flex items-center gap-2 flex-1">
              <h1 className="text-white font-medium text-lg flex-1">
                {currentThread.name ||
                  `New Chat ${new Date(
                    currentThread.created_at
                  ).toLocaleString()}`}
              </h1>
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-8 pt-4 space-y-4 max-w-5xl mx-auto w-full">
        {!currentThread || !chatId ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Bot size={48} className="text-[#1A2235] mb-4" />
            <p className="text-gray-400 mb-4">{t("chat.select_chat")}</p>
            <button
              onClick={handleSelectAssistant}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-[#00C2CB] to-[#6A98F0] text-[#0F1524] hover:opacity-90 transition-colors"
            >
              <span className="font-medium">{t("chat.start_chat")}</span>
            </button>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div
              className="w-[72px] h-[72px] rounded-full overflow-hidden mb-6 bg-gradient-to-br from-[#D6E2E8] to-[#ECF3F5] flex items-center justify-center"
              style={{
                backgroundImage:
                  "linear-gradient(146.84deg, #D6E2E8 -17.54%, #ECF3F5 80.24%)",
                filter: "drop-shadow(0px 0.77px 5.07px rgba(0,0,0,0.25))",
              }}
            >
              <img
                src={defaultBotAvatar}
                alt="Bot Avatar"
                className="w-full h-full object-cover"
              />
            </div>
            <div
              className="p-6 rounded-[24px] bg-transparent max-w-md"
              style={{
                position: "relative",
                backgroundClip: "padding-box",
                borderRadius: "24px",
                border: "1px solid transparent",
                boxShadow: "0px 1.3px 32.7px rgba(0, 0, 0, 0.15)",
                background:
                  "linear-gradient(#0F1524, #0F1524) padding-box, linear-gradient(to bottom, rgba(255,255,255,0.5), rgba(0,194,203,0.8)) border-box",
              }}
            >
              <p className="text-[#00C0FF] text-[12px] font-normal mb-1">
                {currentAssistant?.name || t("chat.universal_assistant")}
              </p>
              <p className="text-white text-[16px] font-light">
                {currentAssistant?.description || t("chat.default_description")}
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="py-4">
              {messages.map((message) => (
                <div
                  key={message.message_id}
                  className={`flex ${
                    message.role === "user" ? "justify-end" : "justify-start"
                  } mb-6`}
                >
                  {message.role !== "user" ? (
                    <div className="flex items-end">
                      <div
                        className="w-8 h-8 rounded-full overflow-hidden mr-2 bg-gradient-to-br from-[#D6E2E8] to-[#ECF3F5] flex items-center justify-center"
                        style={{
                          backgroundImage:
                            "linear-gradient(146.84deg, #D6E2E8 -17.54%, #ECF3F5 80.24%)",
                          filter:
                            "drop-shadow(0px 0.77px 5.07px rgba(0,0,0,0.25))",
                        }}
                      >
                        <img
                          src={defaultBotAvatar}
                          alt="Bot Avatar"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="max-w-[70%] rounded-[15px] rounded-bl-none p-4 bg-[rgba(15,60,85,0.62)] text-white shadow-[0px_1.3px_32.7px_rgba(0,0,0,0.15)]">
                        <div className="mb-2">
                          <span className="text-[#07BEFA] font-medium text-base">
                            {currentAssistant?.name ||
                              t("chat.universal_assistant")}
                          </span>
                        </div>
                        <ReactMarkdown className="prose prose-invert max-w-none prose-p:text-white prose-headings:text-white">
                          {message.role === "assistant" &&
                          !message.content.trim()
                            ? "It seems like there might have been a typo or an unclear message. Could you please clarify your request or question? I'm here to help!"
                            : cleanMessageText(message.content)}
                        </ReactMarkdown>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-end">
                      <div className="max-w-[70%] rounded-xl p-4 bg-[#1A2235] text-white">
                        <ReactMarkdown className="prose prose-invert max-w-none prose-p:text-white prose-headings:text-white">
                          {cleanMessageText(message.content)}
                        </ReactMarkdown>
                      </div>
                      <div
                        className="w-8 h-8 rounded-full overflow-hidden ml-2 bg-[#1A2234] flex items-center justify-center"
                        style={{
                          filter:
                            "drop-shadow(0px 0.77px 5.07px rgba(0,0,0,0.25))",
                        }}
                      >
                        <User size={16} className="text-white" />
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {isWaitingResponse && (
                <div className="flex justify-start mb-6">
                  <div className="flex items-end">
                    <div
                      className="w-8 h-8 rounded-full overflow-hidden mr-2 bg-gradient-to-br from-[#D6E2E8] to-[#ECF3F5] flex items-center justify-center"
                      style={{
                        backgroundImage:
                          "linear-gradient(146.84deg, #D6E2E8 -17.54%, #ECF3F5 80.24%)",
                        filter:
                          "drop-shadow(0px 0.77px 5.07px rgba(0,0,0,0.25))",
                      }}
                    >
                      <img
                        src={defaultBotAvatar}
                        alt="Bot Avatar"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="max-w-[70%] rounded-[15px] rounded-bl-none p-4 bg-[rgba(15,60,85,0.62)] text-white shadow-[0px_1.3px_32.7px_rgba(0,0,0,0.15)]">
                      <div className="mb-2">
                        <span className="text-[#07BEFA] font-medium text-base">
                          {currentAssistant?.name ||
                            t("chat.universal_assistant")}
                        </span>
                      </div>
                      <LoadingDots />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      <div className="bg-[#0F1524] px-8 py-4 flex-shrink-0">
        <div className="max-w-5xl mx-auto w-full">
          <form onSubmit={handleSend}>
            <div className="flex gap-2">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend(e);
                  }
                }}
                placeholder={t("chat.type_message")}
                maxLength={settings.chat.maxMessageLength}
                disabled={
                  isSending || isWaitingResponse || !currentThread || !chatId
                }
                className="flex-1 rounded-xl px-4 py-3 bg-[#1A2235] text-white border border-[#253045] focus:outline-none focus:ring-1 focus:ring-[#00C2CB] focus:border-[#00C2CB] disabled:opacity-50 resize-none"
                ref={textareaRef}
                rows={1}
                style={{ minHeight: "45px" }}
              />
              <button
                type="submit"
                disabled={
                  isSending ||
                  isWaitingResponse ||
                  !newMessage.trim() ||
                  !currentThread ||
                  !chatId
                }
                className="w-[45px] h-[auto] flex items-center justify-center rounded-xl bg-gradient-to-r from-[#00C2CB] to-[#6A98F0] text-[#0F1524] hover:opacity-90 transition-colors disabled:opacity-50"
              >
                <Send size={20} />
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
